/**
 * Builds the `ChatWidgetAPI` handle exposed through the widget's forwarded ref.
 * The method list is the public contract (see `ChatWidgetAPI` in `types/chat.ts`
 * and `CHAT_WIDGET_API_METHODS` below); `mount()` layers `unmount` and
 * `setAnalyticsEnabled` on top of it.
 */
import { useImperativeHandle } from 'react';
import type React from 'react';
import type { ChatWidgetAPI, SessionConfig } from '../types';
import { getSessionId } from '../utils/session';
import type { useChat } from './useChat';
import type { useThread } from './useThread';

/** Every method the ref handle exposes, in declaration order. */
export const CHAT_WIDGET_API_METHODS = [
  'open',
  'close',
  'toggle',
  'createThread',
  'updateThread',
  'deleteThread',
  'createMessage',
  'fetchThread',
  'getSessionId',
  'getThreadId',
  'trackOutcome',
] as const satisfies readonly (keyof ChatWidgetAPI)[];

export interface UseImperativeApiOptions {
  /** Opens the panel as a user-initiated action (may move focus into the composer). */
  openFromUser: () => void;
  closeChat: () => void;
  minimized: boolean;
  thread: Pick<
    ReturnType<typeof useThread>,
    'createThread' | 'updateThread' | 'deleteThread' | 'fetchThread' | 'threadId'
  >;
  createMessage: ReturnType<typeof useChat>['createMessage'];
  sessionConfig?: SessionConfig;
  trackOutcome: ChatWidgetAPI['trackOutcome'];
}

export function useImperativeApi(
  ref: React.ForwardedRef<ChatWidgetAPI>,
  {
    openFromUser,
    closeChat,
    minimized,
    thread,
    createMessage,
    sessionConfig,
    trackOutcome,
  }: UseImperativeApiOptions
): void {
  // No dependency list on purpose: the handle is rebuilt every render, as it always was.
  useImperativeHandle(ref, () => ({
    open: openFromUser,
    close: closeChat,
    toggle: () => (minimized ? openFromUser() : closeChat()),
    createThread: options => thread.createThread(options, true),
    updateThread: thread.updateThread,
    deleteThread: thread.deleteThread,
    createMessage,
    fetchThread: thread.fetchThread,
    getSessionId: () => getSessionId(sessionConfig),
    getThreadId: () => thread.threadId,
    trackOutcome,
  }));
}
